import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bot, Eye, Loader2, Plus, Sparkles, Trash2, UploadCloud } from 'lucide-react';
import { useApiClient } from '../lib/apiClient';
import { useI18n } from '../i18n/LanguageContext';
import {
  formatManualTestDate,
  type ManualTestConfigResponse,
  type ManualTestDefinition,
  type ManualTestEntry,
  type ManualTestImageInference,
} from '../lib/manualTests';

type FormState = {
  testCode: string;
  measuredAt: string;
  valueText: string;
  valueNumber: string;
  unit: string;
  category: string;
  notes: string;
  fields: Record<string, unknown>;
  imageUrl: string;
  imageStorageKey: string;
  imageFileName: string;
};

function nowForInput() {
  const date = new Date();
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60_000);
  return local.toISOString().slice(0, 16);
}

function makeInitialForm(definition?: ManualTestDefinition): FormState {
  return {
    testCode: definition?.code || '',
    measuredAt: nowForInput(),
    valueText: '',
    valueNumber: '',
    unit: definition?.defaultUnit || '',
    category: definition?.category || '',
    notes: '',
    fields: {},
    imageUrl: '',
    imageStorageKey: '',
    imageFileName: '',
  };
}

function hasNumber(value: string) {
  const numeric = Number(value);
  return Number.isFinite(numeric);
}

export function PatientManualTestsModule({ patientId }: { patientId: string }) {
  const { fetchJson, fetchApi } = useApiClient();
  const { tr } = useI18n();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [definitions, setDefinitions] = useState<ManualTestDefinition[]>([]);
  const [entries, setEntries] = useState<ManualTestEntry[]>([]);
  const [form, setForm] = useState<FormState>(makeInitialForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [busyEntryId, setBusyEntryId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const selectedDefinition = useMemo(
    () => definitions.find((definition) => definition.code === form.testCode) || null,
    [definitions, form.testCode],
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [defs, rows] = await Promise.all([
        fetchJson<ManualTestConfigResponse>('/manual-tests/definitions'),
        fetchJson<{ data: ManualTestEntry[] }>(`/manual-tests/patients/${patientId}/entries?limit=100`),
      ]);
      const nextDefinitions = Array.isArray(defs.definitions) ? defs.definitions : [];
      setDefinitions(nextDefinitions);
      setEntries(Array.isArray(rows.data) ? rows.data : []);
      setForm((prev) => {
        const currentExists = nextDefinitions.some((definition) => definition.code === prev.testCode);
        if (currentExists) return prev;
        return makeInitialForm(nextDefinitions[0]);
      });
    } catch (err: any) {
      setError(err?.message || tr('Failed to load manual tests.'));
    } finally {
      setLoading(false);
    }
  }, [fetchJson, patientId, tr]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!dialogOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDialogOpen(false);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [dialogOpen]);

  function openCreateDialog() {
    setForm(makeInitialForm(definitions[0]));
    setError(null);
    setBanner(null);
    setDialogOpen(true);
  }

  async function uploadImage(file: File) {
    setUploading(true);
    setError(null);
    setBanner(null);
    try {
      const presign = await fetchJson<{ key: string; uploadUrl: string; url: string }>('/storage/presign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: file.name,
          contentType: file.type || 'application/octet-stream',
          kind: 'patient-document',
          patientId,
        }),
      });
      const uploadRes = await fetch(presign.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!uploadRes.ok) throw new Error(`Image upload failed (${uploadRes.status})`);
      setForm((prev) => ({
        ...prev,
        imageUrl: presign.url,
        imageStorageKey: presign.key,
        imageFileName: file.name,
      }));
      setBanner(tr('Image uploaded.'));
    } catch (err: any) {
      setError(err?.message || tr('Image upload failed.'));
    } finally {
      setUploading(false);
    }
  }

  async function runAiAssist() {
    if (!form.imageStorageKey && !form.imageUrl) {
      setError(tr('Upload an image before running AI analysis.'));
      return;
    }
    setAnalyzing(true);
    setError(null);
    setBanner(null);
    try {
      const inference = await fetchJson<ManualTestImageInference>(`/manual-tests/patients/${patientId}/analyze-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageStorageKey: form.imageStorageKey || undefined,
          imageUrl: form.imageUrl || undefined,
          hintTestCode: form.testCode || undefined,
          prompt: selectedDefinition?.aiPrompt || undefined,
        }),
      });
      setForm((prev) => ({
        ...prev,
        testCode: inference.testCode || prev.testCode,
        valueText: inference.valueText || prev.valueText,
        valueNumber: inference.valueNumber !== null && inference.valueNumber !== undefined ? String(inference.valueNumber) : prev.valueNumber,
        unit: inference.unit || prev.unit,
        category: inference.category || prev.category,
        fields: {
          ...prev.fields,
          ...(inference.fields || {}),
        },
      }));
      setBanner(
        inference.summary
          ? `${tr('AI analyzed image')} (${inference.confidence !== null && inference.confidence !== undefined ? `${Math.round(inference.confidence * 100)}% ${tr('confidence')}` : tr('confidence not provided')}): ${inference.summary}`
          : tr('AI analysis applied.'),
      );
    } catch (err: any) {
      setError(err?.message || tr('AI analysis failed.'));
    } finally {
      setAnalyzing(false);
    }
  }

  async function createEntry() {
    if (!form.testCode) {
      setError(tr('Select a test type before saving.'));
      return;
    }
    setSaving(true);
    setError(null);
    setBanner(null);
    try {
      const response = await fetchApi(`/manual-tests/patients/${patientId}/entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          testCode: form.testCode,
          measuredAt: new Date(form.measuredAt).toISOString(),
          valueText: form.valueText || undefined,
          valueNumber: hasNumber(form.valueNumber) ? Number(form.valueNumber) : undefined,
          unit: form.unit || undefined,
          category: form.category || undefined,
          notes: form.notes || undefined,
          fields: form.fields,
          imageUrl: form.imageUrl || undefined,
          imageStorageKey: form.imageStorageKey || undefined,
          autoClassify: true,
        }),
      });
      if (!response.ok) throw new Error(`Save failed (${response.status})`);
      const created = (await response.json()) as ManualTestEntry;
      setEntries((prev) => [created, ...prev]);
      setForm((prev) => ({
        ...makeInitialForm(selectedDefinition || undefined),
        testCode: prev.testCode,
      }));
      setBanner(tr('Manual test entry saved.'));
      setDialogOpen(false);
    } catch (err: any) {
      setError(err?.message || tr('Failed to save manual test entry.'));
    } finally {
      setSaving(false);
    }
  }

  async function deleteEntry(entry: ManualTestEntry) {
    if (!window.confirm(tr('Delete this manual test entry?'))) return;
    setBusyEntryId(entry.id);
    setError(null);
    setBanner(null);
    try {
      const response = await fetchApi(`/manual-tests/patients/${patientId}/entries/${entry.id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(`Delete failed (${response.status})`);
      setEntries((prev) => prev.filter((row) => row.id !== entry.id));
      setBanner(tr('Entry deleted.'));
    } catch (err: any) {
      setError(err?.message || tr('Failed to delete entry.'));
    } finally {
      setBusyEntryId(null);
    }
  }

  async function openImage(entry: ManualTestEntry) {
    setBusyEntryId(entry.id);
    setError(null);
    try {
      const payload = await fetchJson<{ previewUrl: string }>(`/manual-tests/patients/${patientId}/entries/${entry.id}/image-preview`);
      window.open(payload.previewUrl, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setError(err?.message || tr('Unable to open image.'));
    } finally {
      setBusyEntryId(null);
    }
  }

  if (loading) {
    return <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500">{tr('Loading manual tests...')}</div>;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">{tr('Manual Tests')}</p>
            <h3 className="text-base font-semibold text-slate-900">{tr('Image and strip-based patient tests')}</h3>
          </div>
          <button
            className="inline-flex items-center gap-2 rounded-xl bg-lime-400 px-3 py-2 text-sm font-medium text-slate-900 hover:bg-lime-300"
            onClick={openCreateDialog}
          >
            <Plus size={14} />
            {tr('Add Manual Test')}
          </button>
        </div>

        <div className="px-4 py-3 text-sm text-slate-600">
          {tr('Definitions come from Settings. Upload test images and optionally let AI classify the result.')}
        </div>

        {error ? <div className="mx-4 mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div> : null}
        {banner ? <div className="mx-4 mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{banner}</div> : null}

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">{tr('Date')}</th>
                <th className="px-4 py-3 font-medium">{tr('Test')}</th>
                <th className="px-4 py-3 font-medium">{tr('Value')}</th>
                <th className="px-4 py-3 font-medium">AI</th>
                <th className="px-4 py-3 text-right font-medium">{tr('Actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td className="px-4 py-3 text-slate-600">{formatManualTestDate(entry.measuredAt)}</td>
                  <td className="px-4 py-3 text-slate-900">
                    <p className="font-medium">{entry.testName}</p>
                    <p className="text-xs text-slate-500">{entry.testCode}</p>
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {entry.valueText || entry.valueNumber !== null ? (
                      <span>
                        {entry.valueText || entry.valueNumber}
                        {entry.unit ? ` ${entry.unit}` : ''}
                      </span>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {entry.aiSummary ? (
                      <span className="text-xs">
                        {entry.aiConfidence !== null ? `${Math.round(entry.aiConfidence * 100)}%` : tr('AI')} {entry.aiSummary}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-2">
                      <button
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-700 disabled:opacity-60"
                        onClick={() => openImage(entry)}
                        disabled={busyEntryId === entry.id || (!entry.imageStorageKey && !entry.imageUrl)}
                      >
                        {busyEntryId === entry.id ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />}
                        {tr('Image')}
                      </button>
                      <button
                        className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-2 py-1 text-xs text-rose-700 disabled:opacity-60"
                        onClick={() => deleteEntry(entry)}
                        disabled={busyEntryId === entry.id}
                      >
                        <Trash2 size={12} />
                        {tr('Delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!entries.length && (
                <tr>
                  <td className="px-4 py-6 text-slate-500" colSpan={5}>
                    {tr('No manual tests recorded yet.')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {dialogOpen && (
        <div className="fixed inset-0 z-40 flex items-start justify-center bg-black/40 p-4" onClick={() => setDialogOpen(false)}>
          <div className="mt-8 max-h-[90vh] w-full max-w-6xl overflow-auto rounded-2xl border border-slate-200 bg-white p-4 shadow-xl" onClick={(event) => event.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between gap-2">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">{tr('Manual Tests')}</p>
                <h4 className="text-lg font-semibold text-slate-900">{tr('Add Manual Test Entry')}</h4>
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 disabled:opacity-60"
                  onClick={runAiAssist}
                  disabled={analyzing || uploading || !form.imageStorageKey}
                >
                  {analyzing ? <Loader2 size={14} className="animate-spin" /> : <Bot size={14} />}
                  {analyzing ? tr('Analyzing...') : tr('AI Analyze Image')}
                </button>
                <button className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700" onClick={() => setDialogOpen(false)}>
                  {tr('Close')}
                </button>
              </div>
            </div>

            {error ? <div className="mb-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div> : null}
            {banner ? <div className="mb-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{banner}</div> : null}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <label className="space-y-1 block">
                <span className="text-xs uppercase tracking-wide text-slate-500">{tr('Test Type')}</span>
                <select
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  value={form.testCode}
                  onChange={(event) => {
                    const definition = definitions.find((row) => row.code === event.target.value);
                    setForm((prev) => ({
                      ...prev,
                      testCode: event.target.value,
                      unit: definition?.defaultUnit || prev.unit,
                      category: definition?.category || prev.category,
                    }));
                  }}
                >
                  <option value="">{tr('Select test')}</option>
                  {definitions.map((definition) => (
                    <option key={definition.code} value={definition.code}>
                      {definition.name}
                    </option>
                  ))}
                </select>
              </label>
              <TextInput
                label={tr('Measured At')}
                type="datetime-local"
                value={form.measuredAt}
                onChange={(value) => setForm((prev) => ({ ...prev, measuredAt: value }))}
              />
              <TextInput
                label={selectedDefinition?.valueLabel || tr('Value (text)')}
                value={form.valueText}
                onChange={(value) => setForm((prev) => ({ ...prev, valueText: value }))}
                placeholder={tr('Trace / Moderate / etc')}
              />
              <TextInput
                label={tr('Value (number)')}
                type="number"
                value={form.valueNumber}
                onChange={(value) => setForm((prev) => ({ ...prev, valueNumber: value }))}
              />
              <TextInput
                label={tr('Unit')}
                value={form.unit}
                onChange={(value) => setForm((prev) => ({ ...prev, unit: value }))}
                placeholder={selectedDefinition?.defaultUnit || ''}
              />
              <TextInput
                label={tr('Category')}
                value={form.category}
                onChange={(value) => setForm((prev) => ({ ...prev, category: value }))}
                placeholder={selectedDefinition?.category || ''}
              />
            </div>

            {!!selectedDefinition?.fields.length && (
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                {selectedDefinition.fields.map((field) => (
                  <DynamicFieldInput
                    key={field.id}
                    field={field}
                    value={form.fields[field.id]}
                    onChange={(value) =>
                      setForm((prev) => ({
                        ...prev,
                        fields: {
                          ...prev.fields,
                          [field.id]: value,
                        },
                      }))
                    }
                  />
                ))}
              </div>
            )}

            <div className="mt-2 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] gap-2">
              <label className="space-y-1 block">
                <span className="text-xs uppercase tracking-wide text-slate-500">{tr('Notes')}</span>
                <textarea
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm min-h-[84px]"
                  value={form.notes}
                  onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
                />
              </label>
              <div className="space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) => {
                    const nextFile = event.target.files?.[0];
                    event.currentTarget.value = '';
                    if (nextFile) uploadImage(nextFile);
                  }}
                />
                <button
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 disabled:opacity-60"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
                  {uploading ? tr('Uploading...') : tr('Upload Image')}
                </button>
                {form.imageFileName ? <p className="text-xs text-slate-500">{form.imageFileName}</p> : <p className="text-xs text-slate-500">{tr('No image')}</p>}
                <button
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-ink px-3 py-2 text-sm text-white disabled:opacity-60"
                  onClick={createEntry}
                  disabled={saving || !form.testCode}
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  {saving ? tr('Saving...') : tr('Save Entry')}
                </button>
                <button className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700" onClick={() => setDialogOpen(false)}>
                  {tr('Cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TextInput({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: 'text' | 'number' | 'datetime-local';
}) {
  return (
    <label className="space-y-1 block">
      <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
      <input
        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function DynamicFieldInput({
  field,
  value,
  onChange,
}: {
  field: ManualTestDefinition['fields'][number];
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const { tr } = useI18n();
  const stringValue = value === null || value === undefined ? '' : String(value);
  if (field.type === 'select') {
    return (
      <label className="space-y-1 block">
        <span className="text-xs uppercase tracking-wide text-slate-500">{field.label}</span>
        <select className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" value={stringValue} onChange={(event) => onChange(event.target.value)}>
          <option value="">{tr('Select')}</option>
          {(field.options || []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </label>
    );
  }
  if (field.type === 'textarea') {
    return (
      <label className="space-y-1 block">
        <span className="text-xs uppercase tracking-wide text-slate-500">{field.label}</span>
        <textarea
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm min-h-[84px]"
          value={stringValue}
          onChange={(event) => onChange(event.target.value)}
        />
      </label>
    );
  }
  return (
    <label className="space-y-1 block">
      <span className="text-xs uppercase tracking-wide text-slate-500">{field.label}</span>
      <input
        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
        value={stringValue}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}
